import { APP_CONSTANTS } from "@core/constants/app-constants";
import { getLogger } from "@core/logging/logger";
import { NewUnsentNotificationData, Notification, NotificationId } from "@domain/notification/notification";
import { NotificationService } from "@domain/notification/notification-service";
import { isNil } from "@utils/assert";
import { observable } from "micro-observables";

export class NotificationManager {
	private readonly logger = getLogger("💬 [NotificationManager]");
	private readonly _notifications = observable<Map<NotificationId, Notification>>(new Map());
	notifications = this._notifications.readOnly();

	private readonly _unreadCount = observable<number>(0);
	private _isFetchingUnsentNotifications = false;
	unreadCount = this._unreadCount.readOnly();

	private _newUnsentNotificationData: NewUnsentNotificationData | undefined;

	private listingOffset = 0;

	_isNewUnsentNotificationsAvailable = observable<boolean>(false);
	isNewUnsentNotificationsAvailable = this._isNewUnsentNotificationsAvailable.readOnly();

	constructor(private readonly notificationService: NotificationService) {
		this._notifications.subscribe(() => this.logger.info("notifications was modified"));
	}

	async loadNotifications(): Promise<{ reachedEnd: boolean }> {
		this.listingOffset = 0;
		this._notifications.set(new Map());
		return this.fetchAndAppendNotifications();
	}

	async loadMoreNotifications(): Promise<{ reachedEnd: boolean }> {
		this.listingOffset += APP_CONSTANTS.NOTIFICATION_CENTER_PAGINATION_SIZE;
		return this.fetchAndAppendNotifications();
	}

	async markAllAsRead(): Promise<void> {
		await this.notificationService.markAllAsRead();
		this._notifications.update(notifications => {
			notifications.forEach(notification => notification.markAsRead());
			return new Map([...notifications]);
		});
		this.updateUnreadCount(0);
	}

	async markAsRead(notificationId: NotificationId, optimistically = true): Promise<void> {
		await this.notificationService.markAsRead(notificationId);
		this._notifications.update(notifications => {
			[...notifications.values()].find(({ id }) => id === notificationId)?.markAsRead();
			return new Map([...notifications]);
		});
		if (optimistically) {
			this.updateUnreadCount(this.unreadCount.get() - 1);
		}
	}

	async markAsUnread(notificationId: NotificationId, optimistically = true): Promise<void> {
		await this.notificationService.markAsUnread(notificationId);
		this._notifications.update(notifications => {
			[...notifications.values()].find(({ id }) => id === notificationId)?.markAsUnread();
			return new Map([...notifications]);
		});
		if (optimistically) {
			this.updateUnreadCount(this.unreadCount.get() + 1);
		}
	}

	async fetchUnsentNotifications(optimistically = false): Promise<void> {
		if (!this._isFetchingUnsentNotifications) {
			this._isFetchingUnsentNotifications = true;
			// Fetch and prepend the new notifications
			const newNotifications = await this.notificationService.getUnsentNotifications();
			const newUnsentCount = newNotifications.length;
			this._notifications.update(
				current =>
					new Map([
						...newNotifications.map<[NotificationId, Notification]>(notification => [notification.id, notification]),
						...current,
					]),
			);
			this.listingOffset += newUnsentCount;

			if (newUnsentCount === 1) {
				this._newUnsentNotificationData = { count: 1, notification: newNotifications[0] };
			} else if (newUnsentCount > 1) {
				this._newUnsentNotificationData = { count: newUnsentCount };
			}
			this.logger.debug("isNewUnreadNotificationsAvailable set to", true);
			this._isNewUnsentNotificationsAvailable.set(true);
			this._isFetchingUnsentNotifications = false;
			if (optimistically) {
				this.updateUnreadCount(this.unreadCount.get() + newUnsentCount);
			}
		}
	}

	get newUnsentNotificationData(): NewUnsentNotificationData | undefined {
		if (isNil(this._newUnsentNotificationData)) {
			return undefined;
		}
		const data = this._newUnsentNotificationData;
		this._isNewUnsentNotificationsAvailable.set(false);
		this.logger.debug("isNewUnreadNotificationsAvailable set to", false);
		this._newUnsentNotificationData = undefined;
		return data;
	}

	updateUnreadCount(unreadCount: number): void {
		if (unreadCount !== this.unreadCount.get()) {
			this.logger.debug("Updating unreadCount from", this.unreadCount.get(), "to", unreadCount);
			this._unreadCount.set(unreadCount);
		}
	}

	debugMockNotificationData(notification: Notification): void {
		this._newUnsentNotificationData = {
			count: 1,
			notification,
		};
		this._isNewUnsentNotificationsAvailable.set(true);
	}

	private async fetchAndAppendNotifications(): Promise<{ reachedEnd: boolean }> {
		const { notifications, reachedEnd, unreadNotificationsCount } = await this.notificationService.loadNotifications(
			APP_CONSTANTS.NOTIFICATION_CENTER_PAGINATION_SIZE,
			this.listingOffset,
		);
		this._notifications.update(
			current =>
				new Map([
					...current,
					...notifications.map<[NotificationId, Notification]>(notification => [notification.id, notification]),
				]),
		);
		this.updateUnreadCount(unreadNotificationsCount);
		return { reachedEnd };
	}

	async reset(): Promise<void> {
		this.logger.debug("Resetting...");
		this._unreadCount.set(0);
		this._isNewUnsentNotificationsAvailable.set(false);
		this._notifications.set(new Map());
		this._newUnsentNotificationData = undefined;
		this.listingOffset = 0;
	}
}
